import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, PhoneCall } from 'lucide-react';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const [status, setStatus] = useState(null);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const response = await fetch('https://api.snapremedy.com/api/contact/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        setStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          company: '',
          phone: '',
          message: '',
        });
      } else {
        setStatus('error');
        console.error('Server error:', result.error);
      }
    } catch (err) {
      setStatus('error');
      console.error('Network error:', err);
    }
  };

  const contactInfo = [
    {
      title: 'Email',
      value: 'support@snapremedy.com',
      link: 'mailto:support@snapremedy.com',
      icon: Mail
    },
    {
      title: 'Phone',
      value: '437-886-6003',
      link: 'tel:+14378866003',
      icon: Phone,
      subtitle: 'Toronto local'
    },
    {
      title: 'Toll Free',
      value: '1-888-SNAP-FIX',
      link: 'tel:+18887627349',
      icon: PhoneCall,
      subtitle: '1-888-762-7349'
    }
  ];

  return (
    <div className="contact">
      <section className="hero">
        <div className="hero-container">
          <motion.div className="contact-hero-content">
            <div className="hero-label">
              <span className="label-dot"></span>
              Get in Touch
            </div>

            <h1 className="hero-title">
              We're here <br />
              <span className="highlight">for you</span>
            </h1>

            <p className="hero-description">
              Ready to simplify your technology and move your business forward? Snap Remedy is here to help. Whether you’re dealing with ongoing issues, planning upgrades, or looking for a more reliable IT partner, our team is ready to step in. Reach out today to start a conversation and see how we can support your goals with practical, dependable solutions.
            </p>
          </motion.div>

          <div>
            <img
              src="hero-snap-remedy-contact.svg"
              className="hero-img"
              alt="Illustration of people and a big lightbulb"
            />
          </div>
        </div>
      </section>

      <section className="contact-section section-spacing" id="contact-form">
        <div className="container">
          <div className="contact-grid">
            <motion.div className="contact-info">
              <div className="info-items">
                {contactInfo.map((item, index) => {
                  const Icon = item.icon;

                  return (
                    <div key={index} className="info-item">
                      <div className="info-icon">
                        <Icon size={20} strokeWidth={1.8} aria-hidden="true" />
                      </div>

                      <div className="info-content">
                        <h3 className="info-item-title">{item.title}</h3>

                        {item.link ? (
                          <a href={item.link} className="info-value">
                            {item.value}
                          </a>
                        ) : (
                          <p className="info-value">{item.value}</p>
                        )}

                        {item.subtitle && (
                          <p className="info-subtitle">{item.subtitle}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            <motion.div className="contact-form-wrapper">
              <h2 className="form-title">Send us a direct message</h2>

              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">First name*</label>
                    <input
                      type="text"
                      name="firstName"
                      className="form-input"
                      placeholder="Your first name"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Last name*</label>
                    <input
                      type="text"
                      name="lastName"
                      className="form-input"
                      placeholder="Your last name"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Company</label>
                  <input
                    type="text"
                    name="company"
                    className="form-input"
                    placeholder="Your company (optional)"
                    value={formData.company}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email*</label>
                  <input
                    type="email"
                    name="email"
                    className="form-input"
                    placeholder="Your e-mail address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    className="form-input"
                    placeholder="Your phone number (optional)"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Your message</label>
                  <textarea
                    name="message"
                    className="form-textarea"
                    placeholder="Tell us how we can help..."
                    value={formData.message}
                    onChange={handleChange}
                    rows="6"
                  ></textarea>
                </div>
                
                {status === 'sending' && (
                  <p className="form-status">Sending your message...</p>
                )}
                {status === 'success' && (
                  <p className="form-status form-status--success">
                    ✓ Message sent! We'll be in touch soon.
                  </p>
                )}
                {status === 'error' && (
                  <p className="form-status form-status--error">
                    Something went wrong. Please try again or email us directly.
                  </p>
                )}
                <button type="submit" className="form-submit" disabled={status === 'sending'}>
                  {status === 'sending' ? 'Sending...' : 'Send Message →'}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;